import type { Application } from "@/lib/api/envoy";
import type { TailoredResumeSummary } from "@/lib/api/resumeService";
import { applyLink } from "@/lib/applications";
import { initialOf } from "@/lib/format";

/** The envoy page's tailor history: each resume written, what for and when. */

/** How many tailors a page of the history shows. */
export const TAILOR_HISTORY_PAGE = 10;

export interface TailorRow {
    resumeId: string;
    postingId: string;
    initial: string;
    /** The posting's title and company, from envoy; a stand-in until it answers. */
    title: string;
    company: string | null;
    /** Where to apply: the extension fills the form there. Null when there is no web address. */
    applyUrl: string | null;
    /** "26 Oct 2026". */
    day: string;
    /** "14:05", in the person's own zone. */
    time: string;
    /** The full instant, for a tooltip and the machine. */
    at: string;
    /**
     * Whether this is the resume the extension fills the application's
     * form with: the newest written for it. Null while the application is
     * not known.
     */
    fills: boolean | null;
    /** Whether it can be deleted: its application is finished, so nothing waits on it. */
    deletable: boolean;
}

/** The statuses an application needs no resume in any more, as the resume service holds them. */
const FINISHED: readonly string[] = ["APPLIED", "SKIPPED", "CLOSED"];

function dayOf(value: Date, timeZone?: string): string {
    return new Intl.DateTimeFormat("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
        timeZone,
    })
        .format(value)
        .replace("Sept", "Sep");
}

function timeOf(value: Date, timeZone?: string): string {
    return new Intl.DateTimeFormat("en-GB", {
        hour: "2-digit",
        minute: "2-digit",
        hourCycle: "h23",
        timeZone,
    }).format(value);
}

/** Each tailored resume as a row, with its posting from `applications`, keyed by posting. */
export function tailorRows(
    tailored: readonly TailoredResumeSummary[],
    applications: Readonly<Record<string, Application>> | undefined,
    timeZone?: string,
): TailorRow[] {
    return tailored.map((entry) => {
        const application = applications?.[entry.posting_id];
        const when = new Date(entry.stored_at);
        const known = !Number.isNaN(when.getTime());
        const title = application?.title || "A posting";
        return {
            resumeId: entry.resume_id,
            postingId: entry.posting_id,
            initial: initialOf(application?.company || title),
            title,
            company: application?.company || null,
            applyUrl: application ? (applyLink(application) ?? null) : null,
            day: known ? dayOf(when, timeZone) : entry.stored_at,
            time: known ? timeOf(when, timeZone) : "",
            at: entry.stored_at,
            fills: application ? application.resume_id === entry.resume_id : null,
            deletable: application ? FINISHED.includes(application.status) : false,
        };
    });
}
